import React from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Carousel, { CarouselItem } from 'tbk-components/src/components/Carousel';
import { IImage } from 'tbk-components/src/components/Image';
import AnimatedArcs from '../AnimatedArcs';
import AdvancedMedia from '../AdvancedMedia';

export interface IImageCarouselProps extends IBaseElementProps {
	/**
	 * A unique identifier assigned automatically the first time the component is rendered.
	 *
	 * @control uniqueid
	 * @prefix image-carousel
	 */
	id?: string;
	/**
	 * Heading
	 */
	heading?: string;
	/**
	 * Intro Text
	 */
	introText?: string;
	/**
	 * Carousel Image Items - Recommended Size for Images: 392px x 294px
	 */
	carouselItems?: IImage[];
	/**
	 * Show Line Animation
	 */
	enableLineAnimations?: boolean;
	/**
	 * Line Animation Delay in Milliseconds
	 */
	lineAnimationDelay?: number;
	/**
	 * Line Animation Offset in Pixils
	 */
	lineAnimationOffset?: number;
}

/**
 * Image Carousel
 * @block
 * @icon images-alt
 */
const ImageCarousel: React.FC<IImageCarouselProps> = ({
	className,
	classNames = [],
	id,
	heading,
	introText,
	carouselItems = [],
	enableLineAnimations = true,
	lineAnimationDelay = 1000,
	lineAnimationOffset = 150,
}) => {
	if (!Array.isArray(carouselItems)) {
		return null;
	}

	return (
		<div
			className={className || cn('calian-image-carousel', ...classNames)}
		>
			<div className="calian-image-carousel__container container-fluid">
				{(heading || introText) && (
					<div className="calian-image-carousel__top row">
						<div className="calian-image-carousel__top-content col-12">
							{heading && (
								<h2 className="calian-image-carousel__heading">
									{heading}
								</h2>
							)}
							{introText && (
								<p className="calian-image-carousel__intro-text">
									{introText}
								</p>
							)}
						</div>
					</div>
				)}
				<div className="calian-image-carousel__bottom row">
					<div className="col-12">
						<div className="calian-image-carousel__bottom-container container-fluid">
							{carouselItems.length > 0 && (
								<Carousel
									className={
										className ||
										cn('calian-image-carousel__carousel')
									}
									showArrows
									showBullets={false}
									perView={3}
									id={id}
									options={{
										type: 'carousel',
										bound: true,
										rewind: false,
										gap: 0,
										breakpoints: {
											1440: {
												type: 'slider',
												perView: 3,
												gap: 16,
											},
											1200: {
												perView: 3,
												gap: 16,
											},
											992: {
												perView: 2,
												gap: 16,
											},
											576: {
												perView: 1,
												gap: 16,
											},
										},
									}}
								>
									{carouselItems.map((item, index) => (
										<CarouselItem key={index}>
											<AdvancedMedia
												size="4:3"
												classNames={[
													'calian-image-carousel__img',
												]}
												image={item}
											/>
										</CarouselItem>
									))}
								</Carousel>
							)}
						</div>
					</div>
				</div>
				{enableLineAnimations && (
					<AnimatedArcs
						arcsStyle={'Component - Two Lines'}
						arcsPosition="Left"
						arcOneColor="var(--bs-primary)"
						arcOneStrokeWidth={4}
						arcTwoColor="var(--bs-white)"
						arcTwoStrokeWidth={3}
						animationDelay={lineAnimationDelay}
						animationOffset={lineAnimationOffset}
					/>
				)}
			</div>
		</div>
	);
};

export default ImageCarousel;
